import { makeAutoObservable, reaction, runInAction } from "mobx";
import { APIPROXY_HOST } from "../common/environment";
import { logger } from "../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import {
   type ServerEventListener,
   type ServerEventMessageType,
   serverEventMessageTypes,
   type WsStatus
} from "../common/zodSchemas/serverEvents";
import authStore from "./auth/authStore";

type ServerEventStore = BaseStoreType & {
   status: WsStatus;
   socket: WebSocket | null;
   currentCustomerNumber: string | null;
   init: () => void;
   connect: (customerNumber: string) => void;
   disconnect: () => void;
   addListener: <T extends ServerEventMessageType>(type: T, listener: ServerEventListener) => void;
   removeListener: <T extends ServerEventMessageType>(type: T, listener: ServerEventListener) => void;
};

const protocol: "wss:" | "ws:" = (APIPROXY_HOST || "").startsWith("https") ? "wss:" : "ws:";

const url = resolvePlatformRequestUrl(platformRoutes.serverEvent, { protocol });

const initialListeners = () => ({
   delivery_status: [],
   order_delivered: []
});

const maxReconnectTimeoutMs = 5 * 60 * 1000; // 5 minutes
let currentReconnectTimeoutMs: number = 1000; // 1 second
let currentReconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

const getReconnectTimeout = () => {
   const timeout = currentReconnectTimeoutMs;
   currentReconnectTimeoutMs = Math.min(currentReconnectTimeoutMs * 2, maxReconnectTimeoutMs);
   return timeout;
};

const listeners: Record<ServerEventMessageType, ServerEventListener[]> = initialListeners();

const serverEventStore: ServerEventStore = makeAutoObservable({
   status: "disconnected",
   socket: null,
   currentCustomerNumber: null,
   init: () => {
      reaction(
         () => authStore.isLoggedIn(),
         (isLoggedIn) => {
            const customerNumber = isLoggedIn ? authStore.currentCompany : null;
            if (customerNumber) {
               serverEventStore.connect(customerNumber);
            } else {
               serverEventStore.disconnect();
            }
         }
      );
   },

   connect: (customerNumber: string) => {
      const token = authStore.getSessionToken();
      const { status } = serverEventStore;
      if (!token || !customerNumber || !APIPROXY_HOST || status === "connecting") {
         return;
      }
      if (serverEventStore.socket) {
         serverEventStore.socket.close();
      }
      serverEventStore.status = "connecting";
      const ws = new WebSocket(url);
      ws.onopen = () => {
         if (serverEventStore.status === "connecting") {
            ws.send(JSON.stringify({ type: "auth", token: `Bearer ${token}`, customerNumber }));
         }
      };
      ws.onmessage = (event: MessageEvent) => {
         const { data } = event;
         try {
            const message = JSON.parse(data);
            const { type } = message;
            if (type === "auth_ok") {
               logger.log("websockets", "Successfully connected to server events websocket");
               runInAction(() => {
                  serverEventStore.status = "connected";
                  currentReconnectTimeoutMs = 1000; // reset reconnect timeout on successful connection
               });
               return;
            }
            logger.log("websockets", "Received message through server events: ", message);
            for (const eventType of serverEventMessageTypes) {
               if (type === eventType) {
                  for (const listener of listeners[eventType]) {
                     listener(message);
                  }
               }
            }
            if (!serverEventMessageTypes.includes(type)) {
               logger.warn("websockets", "unsupported message type", message);
            }
         } catch (_err) {
            logger.warn("websockets", "failed to parse message", data);
         }
      };
      ws.onclose = (e) => {
         if (e.code === 1006) {
            logger.warn(
               "websockets",
               "Websocket connection closed abnormally. This may indicate a network issue or that the server is unavailable."
            );
            const { currentCustomerNumber } = serverEventStore;
            if (serverEventStore.socket === ws) {
               runInAction(() => {
                  serverEventStore.socket = null;
                  serverEventStore.status = "disconnected";
                  if (currentCustomerNumber) {
                     reconnect();
                  }
               });
            }
         } else {
            runInAction(() => {
               if (serverEventStore.socket === ws) {
                  serverEventStore.socket = null;
                  serverEventStore.status = "disconnected";
               }
            });
         }
      };

      ws.onerror = (err) => {
         logger.warn("websockets", "websockets error encountered", err);
         reconnect();
      };
      serverEventStore.socket = ws;
      serverEventStore.currentCustomerNumber = customerNumber;
   },

   addListener: <T extends ServerEventMessageType>(type: T, listener: ServerEventListener) => {
      listeners[type].push(listener);
   },

   removeListener: <T extends ServerEventMessageType>(type: T, listener: ServerEventListener) => {
      listeners[type] = listeners[type].filter((l) => l !== listener);
   },

   disconnect: () => {
      serverEventStore.clearCompanySpecificData();
   },

   clearCompanySpecificData: () => {
      runInAction(() => {
         serverEventStore.socket?.close();
         serverEventStore.status = "disconnected";
         serverEventStore.socket = null;
         serverEventStore.currentCustomerNumber = null;
      });
   }
});

const reconnect = () => {
   const { currentCustomerNumber } = serverEventStore;
   if (!currentReconnectTimeoutId && currentCustomerNumber) {
      logger.log(
         "websockets",
         `Attempting to reconnect to server events websocket in ${currentReconnectTimeoutMs / 1000} seconds...`
      );
      currentReconnectTimeoutId = setTimeout(() => {
         currentReconnectTimeoutId = null;
         if (serverEventStore.currentCustomerNumber === currentCustomerNumber) {
            serverEventStore.connect(currentCustomerNumber);
         }
      }, getReconnectTimeout());
   }
};

// This is here to show that serverEventStore listeners can be added to react to server events.  We can remove this when we have real usage of these events.

serverEventStore.addListener("delivery_status", (message) => {
   logger.log("websockets", "Received delivery status update through server events: ", message);
});
serverEventStore.addListener("order_delivered", (message) => {
   logger.log("websockets", "Received order delivered update through server events: ", message);
});

reaction(
   () => serverEventStore.socket?.readyState,
   (readyState) => {
      logger.log("websockets", "Websocket readyState changed: ", readyState);
      if (readyState === WebSocket.CLOSED && serverEventStore.status === "connected" && serverEventStore.currentCustomerNumber) {
         reconnect();
      }
   }
);

export default serverEventStore;
